import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Car, ChevronLeft, ChevronRight, Images, MessageCircle, Search, SlidersHorizontal, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getProducts } from '../services/api';
import { useCart } from '../context/CartContext';
import usePageSEO from '../hooks/usePageSEO';
import { getWhatsAppProductUrl } from '../lib/whatsapp';
import { isPriceOnRequest } from '../lib/pricing';
import {
  ALL_VALUE,
  buildBrandOptions,
  buildModelOptions,
  buildYearOptions,
  getBrandKey,
  getLocalizedValue,
  getProductImageList,
  getSearchableText,
  getVehicleModel,
  getVehicleYear,
  normalizeSearch,
} from '../lib/productFitment';

const PAGE_SIZE = 24;
const SORT_OPTIONS = new Set(['default', 'name', 'category']);
const SHOP_FILTER_PARAMS = ['search', 'brand', 'model', 'year', 'sort', 'page'];

const getPageFromParams = (params) => {
  const page = parseInt(params.get('page') || '1', 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const getSortFromParams = (params) => {
  const sort = params.get('sort') || 'default';
  return SORT_OPTIONS.has(sort) ? sort : 'default';
};

const Shop = () => {
  const { t, i18n } = useTranslation();
  const { addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const latestSearchParamsRef = useRef(searchParams);
  const searchEditedRef = useRef(false);
  const lang = i18n.language?.substring(0, 2) || 'en';
  const selectDir = lang === 'ar' ? 'rtl' : 'ltr';

  usePageSEO({
    title: t('pages.shopTitle'),
    description: t('pages.shopDesc'),
    keywords: 'car electronics shop, android car screen, car audio, car accessories bahrain'
  });

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('brand') || ALL_VALUE);
  const [selectedModel, setSelectedModel] = useState(searchParams.get('model') || ALL_VALUE);
  const [selectedYear, setSelectedYear] = useState(searchParams.get('year') || ALL_VALUE);
  const [sortBy, setSortBy] = useState(getSortFromParams(searchParams));
  const [currentPage, setCurrentPage] = useState(getPageFromParams(searchParams));
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadCatalog = async () => {
      try {
        setLoading(true);
        const productsData = await getProducts({ limit: 1000, sort_lang: lang });
        if (isMounted) setAllProducts(productsData);
      } catch (error) {
        console.error('Error loading products:', error);
        if (isMounted) setAllProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadCatalog();
    return () => { isMounted = false; };
  }, [lang]);

  useEffect(() => {
    latestSearchParamsRef.current = searchParams;
  }, [searchParams]);

  useEffect(() => {
    const nextSearchQuery = searchParams.get('search') || '';
    setSearchQuery(nextSearchQuery);
    setDebouncedSearchQuery(nextSearchQuery);
    setSelectedCategory(searchParams.get('brand') || ALL_VALUE);
    setSelectedModel(searchParams.get('model') || ALL_VALUE);
    setSelectedYear(searchParams.get('year') || ALL_VALUE);
    setSortBy(getSortFromParams(searchParams));
    setCurrentPage(getPageFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearchQuery = searchQuery.trim();

      if (!searchEditedRef.current) {
        setDebouncedSearchQuery(nextSearchQuery);
        return;
      }

      const currentParams = latestSearchParamsRef.current;
      const nextParams = new URLSearchParams(currentParams);
      if (nextSearchQuery) {
        nextParams.set('search', nextSearchQuery);
      } else {
        nextParams.delete('search');
      }
      nextParams.delete('page');

      if (nextParams.toString() !== currentParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      }
      setCurrentPage(1);
      setDebouncedSearchQuery(nextSearchQuery);
      searchEditedRef.current = false;
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, setSearchParams]);

  const brandOptions = useMemo(() => {
    return buildBrandOptions(allProducts, lang);
  }, [allProducts, lang]);

  const modelOptions = useMemo(() => {
    return buildModelOptions(allProducts, selectedCategory);
  }, [allProducts, selectedCategory]);

  const yearOptions = useMemo(() => {
    return buildYearOptions(allProducts, selectedCategory, selectedModel);
  }, [allProducts, selectedCategory, selectedModel]);

  const filteredProducts = useMemo(() => {
    const search = normalizeSearch(debouncedSearchQuery);
    const products = allProducts.filter((product) => {
      if (selectedCategory !== ALL_VALUE && getBrandKey(product) !== selectedCategory) return false;
      if (selectedModel !== ALL_VALUE && getVehicleModel(product) !== selectedModel) return false;
      if (selectedYear !== ALL_VALUE && getVehicleYear(product) !== selectedYear) return false;
      if (search && !normalizeSearch(getSearchableText(product, lang)).includes(search)) return false;
      return true;
    });

    if (sortBy === 'name') {
      return [...products].sort((a, b) => getLocalizedValue(a.name, lang).localeCompare(getLocalizedValue(b.name, lang)));
    }

    if (sortBy === 'category') {
      return [...products].sort((a, b) => {
        const categoryCompare = getLocalizedValue(a.category, lang).localeCompare(getLocalizedValue(b.category, lang));
        if (categoryCompare !== 0) return categoryCompare;
        return getLocalizedValue(a.name, lang).localeCompare(getLocalizedValue(b.name, lang));
      });
    }

    return products;
  }, [allProducts, debouncedSearchQuery, lang, selectedCategory, selectedModel, selectedYear, sortBy]);

  const totalProducts = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));
  const visibleCurrentPage = Math.min(currentPage, totalPages);
  const firstVisibleProduct = totalProducts === 0 ? 0 : (visibleCurrentPage - 1) * PAGE_SIZE + 1;
  const lastVisibleProduct = Math.min(visibleCurrentPage * PAGE_SIZE, totalProducts);
  const products = filteredProducts.slice((visibleCurrentPage - 1) * PAGE_SIZE, visibleCurrentPage * PAGE_SIZE);
  const pageNumbers = Array.from({ length: totalPages }, (_, idx) => idx + 1)
    .filter(page => totalPages <= 7 || page === 1 || page === totalPages || Math.abs(page - visibleCurrentPage) <= 1);

  const selectedBrandLabel = brandOptions.find((brand) => brand.value === selectedCategory)?.label || '';
  const requestSubject = [
    selectedBrandLabel,
    selectedModel !== ALL_VALUE ? selectedModel : '',
    selectedYear !== ALL_VALUE ? selectedYear : '',
    debouncedSearchQuery ? `"${debouncedSearchQuery}"` : '',
  ].filter(Boolean).join(' ') || t('products.customVehicle');

  const handleSearchChange = (value) => {
    searchEditedRef.current = true;
    setSearchQuery(value);
  };

  const writeFilterParams = ({
    brand = selectedCategory,
    model = selectedModel,
    year = selectedYear,
    sort = sortBy,
  } = {}) => {
    const currentParams = latestSearchParamsRef.current;
    const nextParams = new URLSearchParams(currentParams);

    if (brand && brand !== ALL_VALUE) nextParams.set('brand', brand);
    else nextParams.delete('brand');

    if (model && model !== ALL_VALUE) nextParams.set('model', model);
    else nextParams.delete('model');

    if (year && year !== ALL_VALUE) nextParams.set('year', year);
    else nextParams.delete('year');

    if (sort && sort !== 'default') nextParams.set('sort', sort);
    else nextParams.delete('sort');

    nextParams.delete('page');

    if (nextParams.toString() !== currentParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    const nextPage = Math.max(1, page);
    setCurrentPage(nextPage);
    const nextParams = new URLSearchParams(latestSearchParamsRef.current);
    if (nextPage > 1) {
      nextParams.set('page', String(nextPage));
    } else {
      nextParams.delete('page');
    }
    setSearchParams(nextParams, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSelectedModel(ALL_VALUE);
    setSelectedYear(ALL_VALUE);
    writeFilterParams({ brand: value, model: ALL_VALUE, year: ALL_VALUE });
  };

  const handleModelChange = (value) => {
    setSelectedModel(value);
    setSelectedYear(ALL_VALUE);
    writeFilterParams({ model: value, year: ALL_VALUE });
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    writeFilterParams({ year: value });
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    writeFilterParams({ sort: value });
  };

  const clearFilters = () => {
    searchEditedRef.current = false;
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSelectedCategory(ALL_VALUE);
    setSelectedModel(ALL_VALUE);
    setSelectedYear(ALL_VALUE);
    setSortBy('default');
    setCurrentPage(1);

    const nextParams = new URLSearchParams(latestSearchParamsRef.current);
    SHOP_FILTER_PARAMS.forEach((param) => nextParams.delete(param));
    setSearchParams(nextParams, { replace: true });
  };

  const formatPrice = (product) => {
    if (isPriceOnRequest(product.price_min)) {
      return t('pages.priceOnRequest');
    }
    if (product.price_max) {
      return `${product.price_min.toFixed(3)} - ${product.price_max.toFixed(3)} ${product.currency}`;
    }
    return `${product.price_min.toFixed(3)} ${product.currency}`;
  };

  const canOrderProduct = (product) => !isPriceOnRequest(product.price_min) && product.stock_quantity > 0;
  const getQuoteLabel = (product) => isPriceOnRequest(product.price_min) ? t('pages.contactForPrice') : t('pages.preorderCta');

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('shop.title')}</h1>
          <p className="text-xl text-gray-600">{t('shop.description')}</p>
        </div>

        <div className="mb-8 rounded-lg border bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
              <Car className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('products.vehicleFinderTitle')}</h2>
              <p className="text-sm text-gray-600">{t('products.vehicleFinderDesc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,1fr)_180px_180px_180px_160px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder={t('products.search')}
                className="pl-10"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>

            <Select dir={selectDir} value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger><SelectValue placeholder={t('products.selectBrand')} /></SelectTrigger>
              <SelectContent dir={selectDir}>
                <SelectItem value={ALL_VALUE}>{t('products.allBrands')}</SelectItem>
                {brandOptions.map((brand) => (
                  <SelectItem key={brand.value} value={brand.value}>{brand.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select dir={selectDir} value={selectedModel} onValueChange={handleModelChange}>
              <SelectTrigger><SelectValue placeholder={t('products.selectModel')} /></SelectTrigger>
              <SelectContent dir={selectDir}>
                <SelectItem value={ALL_VALUE}>{t('products.allModels')}</SelectItem>
                {modelOptions.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select dir={selectDir} value={selectedYear} onValueChange={handleYearChange}>
              <SelectTrigger><SelectValue placeholder={t('products.selectYear')} /></SelectTrigger>
              <SelectContent dir={selectDir}>
                <SelectItem value={ALL_VALUE}>{t('products.allYears')}</SelectItem>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select dir={selectDir} value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger><SelectValue placeholder={t('products.sortBy')} /></SelectTrigger>
              <SelectContent dir={selectDir}>
                <SelectItem value="default">{t('products.sortDefault')}</SelectItem>
                <SelectItem value="name">{t('products.sortName')}</SelectItem>
                <SelectItem value="category">{t('products.sortCategory')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-3 flex flex-col gap-2 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
            <p>{t('products.fitmentHelper')}</p>
            <Button onClick={clearFilters} variant="ghost" className="self-start text-red-600 hover:text-red-700 sm:self-auto">
              {t('products.clearFilters')}
            </Button>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-600">
            {loading ? (
              t('products.loadingProducts')
            ) : (
              <>{t('products.showing')} {firstVisibleProduct}-{lastVisibleProduct} {t('products.of')} {totalProducts} {totalProducts !== 1 ? t('products.products') : t('products.product')}</>
            )}
          </p>
        </div>

        <div aria-busy={loading}>
          {loading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" role="status" aria-live="polite">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="h-[430px] animate-pulse rounded-lg border bg-white">
                  <div className="h-56 rounded-t-lg bg-gray-100" />
                  <div className="space-y-3 p-5">
                    <div className="h-5 w-4/5 rounded bg-gray-100" />
                    <div className="h-4 w-full rounded bg-gray-100" />
                    <div className="h-4 w-2/3 rounded bg-gray-100" />
                    <div className="h-10 rounded bg-gray-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {products.map((product) => {
                  const productName = getLocalizedValue(product.name, lang);
                  const productDescription = getLocalizedValue(product.description, lang);
                  const productCategory = getLocalizedValue(product.category, lang);
                  const productImages = getProductImageList(product);
                  return (
                    <Card key={product.id} className="group overflow-hidden transition-all duration-300 hover:shadow-xl">
                      <Link to={`/shop/${product.id}`}>
                        <div className="relative overflow-hidden">
                          <img src={product.image_url} alt={productName} className="h-56 w-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                          <Badge className="absolute right-3 top-3 bg-red-600 text-white">{productCategory}</Badge>
                          {productImages.length > 1 && (
                            <Badge className="absolute left-3 top-3 bg-gray-900/85 text-white">
                              <Images className="h-3 w-3 mr-1" />
                              {productImages.length} {t('products.photos')}
                            </Badge>
                          )}
                        </div>
                        <CardContent className="p-5">
                          <h3 className="mb-2 min-h-[3rem] font-bold text-gray-900 line-clamp-2">{productName}</h3>
                          <p className="mb-3 text-sm text-gray-600 line-clamp-2">{productDescription}</p>
                          <div className="mb-4 flex flex-wrap gap-1">
                            {(product.specs || []).slice(0, 3).map((spec, idx) => (
                              <span key={idx} className="rounded bg-gray-100 px-2 py-1 text-xs">{getLocalizedValue(spec, lang)}</span>
                            ))}
                          </div>
                          <div className="border-t pt-4">
                            <span className="text-lg font-bold text-red-600">{formatPrice(product)}</span>
                          </div>
                        </CardContent>
                      </Link>
                      <div className="px-5 pb-5">
                        {canOrderProduct(product) ? (
                          <Button
                            className="w-full bg-red-600 hover:bg-red-700"
                            onClick={(e) => { e.preventDefault(); addToCart(product.id, 1); }}
                          >
                            <ShoppingCart className="h-4 w-4 mr-2" />
                            {t('pages.addToCart')}
                          </Button>
                        ) : (
                          <Button className="w-full bg-green-600 hover:bg-green-700" asChild>
                            <a href={getWhatsAppProductUrl(productName, product.id, lang)} target="_blank" rel="noopener noreferrer">
                              <MessageCircle className="h-4 w-4 mr-2" />
                              {getQuoteLabel(product)}
                            </a>
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => goToPage(visibleCurrentPage - 1)}
                    disabled={visibleCurrentPage <= 1}
                    aria-label={t('products.previousPage')}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('products.previous')}
                  </Button>
                  {pageNumbers.map((page, index) => {
                    const previousPage = pageNumbers[index - 1];
                    return (
                      <React.Fragment key={page}>
                        {previousPage && page - previousPage > 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          variant={page === visibleCurrentPage ? 'default' : 'outline'}
                          className={page === visibleCurrentPage ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                          onClick={() => goToPage(page)}
                          aria-current={page === visibleCurrentPage ? 'page' : undefined}
                        >
                          {page}
                        </Button>
                      </React.Fragment>
                    );
                  })}
                  <Button
                    variant="outline"
                    onClick={() => goToPage(visibleCurrentPage + 1)}
                    disabled={visibleCurrentPage >= totalPages}
                    aria-label={t('products.nextPage')}
                  >
                    {t('products.next')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="mx-auto rounded-lg border bg-white px-6 py-14 text-center">
              <div className="mb-4 text-gray-400"><SlidersHorizontal className="h-16 w-16 mx-auto" /></div>
              <h3 className="mb-2 text-xl font-bold text-gray-900">{t('products.noProducts')}</h3>
              <p className="mb-6 text-gray-600">{t('products.noProductsDesc')}</p>
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <Button asChild className="bg-green-600 hover:bg-green-700">
                  <a href={getWhatsAppProductUrl(lang === 'ar' ? `توفر ${requestSubject} أو إمكانية الطلب المسبق` : `${requestSubject} availability or preorder`, undefined, lang)} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    {t('products.requestProduct')}
                  </a>
                </Button>
                <Button onClick={clearFilters} variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white">
                  {t('products.clearFilters')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
